import { useState, useEffect, useRef } from "react";
import BaseModal from "@components/modals/BaseModal";
import { DocumentModal } from "@components/modals/DocumentModal";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import TextField from "@components/ui/textfields/TextField";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import DatePicker from "@components/ui/selection/DatePicker";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import Button from "@components/ui/buttons/Button";
import { CustomImage } from "@components/ui/images/CustomImage";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";
import DropDown from "@components/ui/dropdown/DropDown";
import Icon1 from "@assets/icons/Icon";
import {
  DocumentSignature,
  DocumentForm,
  FormField,
} from "types/generated_document";
import { Project } from "types";
import { Timestamp } from "firebase/firestore";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { generatedDocumentsApi } from "@services/api/generatedDocuments";
import { usersApi } from "@services/api/users";
import { showNotification } from "context/notification.service";

const TABS = [
  { value: "infos", label: "Infos" },
  { value: "form", label: "Formulaire" },
  { value: "body", label: "Notes" },
  { value: "photos", label: "Photos" },
  { value: "signataires", label: "Signatures" },
  { value: "generer", label: "Générer" },
];

function buildFolderOptions(project: Project) {
  const options: { value: string; label: string }[] = [];
  for (const folder of project.folders) {
    options.push({ value: folder.id, label: folder.name });
    if (folder.sub_folders) {
      for (const sub of folder.sub_folders) {
        options.push({
          value: `${folder.id}/${sub.id}`,
          label: `${folder.name} / ${sub.name}`,
        });
      }
    }
  }
  return options;
}

interface Props {
  onClose: () => void;
  project: Project;
  setProject: (project: Project) => void;
  orgId: string;
  onSignatureRequestCreated?: () => void;
}

export const CompteRenduModal = ({ onClose, project, setProject, orgId, onSignatureRequestCreated }: Props) => {
  const { formTemplates, fetchFormTemplates, fetchProject } = useProjectStore();
  const userData = useDataStore((state) => state.userData);
  const orgMembers = useDataStore((state) => state.orgMembers);
  const signatureFileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingSignature, setIsUploadingSignature] = useState(false);
  const [uploadedSignatureUrl, setUploadedSignatureUrl] = useState<string | null>(null);

  useEffect(() => {
    if (orgId) fetchFormTemplates(orgId);
  }, []);

  // Fetch fresh signature URL on mount (stored URL may have expired)
  useEffect(() => {
    if (!userData?.id || !userData?.infos?.signature_id) return;
    usersApi.get(userData.id).then((freshUser) => {
      if (freshUser.infos?.signature_id) {
        setUploadedSignatureUrl(freshUser.infos.signature_id);
      }
    }).catch(() => {});
  }, [userData?.id]);

  const [activeTab, setActiveTab] = useState("infos");
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<string[]>([]);

  // Infos
  const conductor = orgMembers.find((m) => m.id === project.infos.team.conductor_id);
  const [title, setTitle] = useState("Compte-rendu de chantier");
  const [projectName, setProjectName] = useState(project.infos.general.name || "");
  const [fullAddress, setFullAddress] = useState(
    [
      project.infos.general.address,
      project.infos.general.postal,
      project.infos.general.city,
    ]
      .filter(Boolean)
      .join(", ")
  );
  const [dateSeconds, setDateSeconds] = useState(Math.floor(Date.now() / 1000));

  // Body
  const [body, setBody] = useState("");

  // Form
  const [includeForm, setIncludeForm] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [form, setForm] = useState<DocumentForm | undefined>(undefined);

  useEffect(() => {
    if (!includeForm) {
      setForm(undefined);
      return;
    }
    if (!selectedTemplateId) return;
    const template = formTemplates.find((t) => t.id === selectedTemplateId);
    if (!template) return;
    setForm({
      id: template.id,
      categories: template.categories.map((cat) => ({
        ...cat,
        fields: cat.fields.map((f) => ({
          ...f,
          value:
            f.type === "checkbox"
              ? false
              : f.type === "multiselect"
              ? []
              : f.type === "date"
              ? Math.floor(Date.now() / 1000)
              : "",
        })),
      })),
    });
  }, [includeForm, selectedTemplateId]);

  function updateFormFieldValue(catId: string, fieldId: string, value: any) {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        categories: prev.categories.map((c) =>
          c.id === catId
            ? {
                ...c,
                fields: c.fields.map((f) =>
                  f.id === fieldId ? { ...f, value } : f
                ),
              }
            : c
        ),
      };
    });
  }

  // Signatures
  const [signatures, setSignatures] = useState<DocumentSignature[]>([]);

  function toggleSignature(
    id: string,
    entry: Omit<DocumentSignature, "id" | "status">,
    status: DocumentSignature["status"] = "pending_remote"
  ) {
    setSignatures((prev) => {
      const exists = prev.find((s) => s.id === id);
      if (exists) return prev.filter((s) => s.id !== id);
      return [...prev, { id, status, ...entry }];
    });
  }

  function toggleConductorSignature() {
    const exists = signatures.find((s) => s.id === conductorId);
    if (exists) {
      setSignatures((prev) => prev.filter((s) => s.id !== conductorId));
      return;
    }
    // Backend will handle resolving the conductor's signature from their profile
    const hasSavedSignature = !!userData?.infos?.signature_id;
    setSignatures((prev) => [
      ...prev,
      {
        id: conductorId,
        signer_type: "conductor",
        signer_id: conductorId,
        signer_name: conductorName,
        signer_email: userData?.infos?.email,
        status: hasSavedSignature ? "signed_in_app" : "pending",
        signature_base64: undefined,
      },
    ]);
  }

  function handleSignatureButtonClick() {
    signatureFileInputRef.current?.click();
  }

  async function handleSignatureUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files || !e.target.files[0] || !userData?.id) return;

    const file = e.target.files[0];

    // Validate file type
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      showNotification({
        message: "Format non supporté. Utilisez JPG, PNG ou WEBP.",
        type: "error",
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showNotification({
        message: "La taille maximale est de 5 Mo.",
        type: "error",
      });
      return;
    }

    setIsUploadingSignature(true);

    try {
      const response = await usersApi.uploadSignature(userData.id, file);

      // Store the uploaded signature URL for immediate preview
      setUploadedSignatureUrl(response.signature_url);

      showNotification({
        message: "Signature téléchargée avec succès !",
        type: "success",
      });

      // Automatically update the conductor signature status to signed_in_app
      setSignatures((prev) =>
        prev.map((s) =>
          s.id === conductorId ? { ...s, status: "signed_in_app" } : s
        )
      );
    } catch (error) {
      console.error("Error uploading signature:", error);
      showNotification({
        message: "Échec du téléchargement de la signature",
        type: "error",
      });
    } finally {
      setIsUploadingSignature(false);
      if (signatureFileInputRef.current) {
        signatureFileInputRef.current.value = "";
      }
    }
  }

  // Folder destination
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const folderOptions = buildFolderOptions(project);

  // Generate
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedDocId, setGeneratedDocId] = useState<string | null>(null);
  const [generatedProject, setGeneratedProject] = useState<Project | null>(null);

  const clients = project.infos.client.contacts || [];
  const partners = project.infos.partners || [];
  const conductorId = userData?.id || "conductor";
  const conductorName = userData?.infos?.display_name || "Moi";

  async function handleGenerate() {
    if (isGenerating) return;
    if (!selectedFolderId) {
      showNotification({
        type: "error",
        message: "Veuillez sélectionner un dossier de destination.",
      });
      return;
    }
    setIsGenerating(true);
    try {
      const photoIds = selectedPhotoIds
        .map((id) => project.photos.find((p) => p.id === id))
        .filter((photo) => photo && photo.file?.id)
        .map((photo) => photo!.file.id as string);

      const generatedDocument = {
        id: "doc_" + Date.now(),
        name: `Compte-rendu - ${project.infos.general.name}`,
        created_at: Timestamp.now(),
        infos: {
          title,
          project_name: projectName,
          full_adress: fullAddress,
          date: Timestamp.fromMillis(dateSeconds * 1000),
          conductor_display_name:
            conductor?.infos?.display_name || conductorName,
        },
        body,
        form,
        signatures: [],
      };

      const response = await generatedDocumentsApi.generate({
        generatedDocument,
        signatures,
        photoIds,
        projectId: project.id,
        orgId,
        folderId: selectedFolderId,
      });

      // Backend has inserted the document into the project folder - refetch to get updated state
      const updatedProject = await fetchProject(project.id);

      if (updatedProject) {
        setProject(updatedProject);
        setGeneratedProject(updatedProject);
      }

      setGeneratedDocId(response.id);
      showNotification({
        type: "success",
        message: "Compte-rendu généré avec succès !",
      });

      // Notify parent to refresh signature requests if CR has pending signatures
      if (signatures.some((s) => s.status !== "signed_in_app")) {
        onSignatureRequestCreated?.();
      }
    } catch {
      showNotification({
        type: "error",
        message: "Erreur lors de la génération du document.",
      });
    } finally {
      setIsGenerating(false);
    }
  }

  const backBtn = (prevTab: string) => (
    <button
      className="text-h3 text-neutral-400 hover:text-neutral-600"
      onClick={() => setActiveTab(prevTab)}
    >
      ← Retour
    </button>
  );

  const nextBtn = (nextTab: string) => (
    <Button label="Suivant" onClick={() => setActiveTab(nextTab)} />
  );

  if (generatedDocId && generatedProject) {
    return (
      <DocumentModal
        document_id={generatedDocId}
        activeProject={generatedProject}
        setActiveProject={setProject}
        onClose={onClose}
      />
    );
  }

  return (
    <BaseModal
      onClose={onClose}
      title="Compte-rendu de chantier"
      startIcon={<Icon1 name="file" size={20} />}
      width="max-w-3xl"
    >
      <div className="flex flex-col gap-6 p-6">
        <ChunkyTabs
          tabs={TABS}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />

        {/* ── INFOS ── */}
        {activeTab === "infos" && (
          <div className="flex flex-col gap-4">
            <p className="text-h3 text-neutral-400">
              Vérifiez les informations du projet.
            </p>
            <TextField
              label="Titre du document"
              value={title}
              onChange={setTitle}
              placeholder="Compte-rendu de chantier"
            />
            <TextField
              label="Nom du projet"
              value={projectName}
              onChange={setProjectName}
            />
            <TextField
              label="Adresse du projet"
              value={fullAddress}
              onChange={setFullAddress}
            />
            <DatePicker
              label="Date du document"
              seconds={dateSeconds}
              onChange={setDateSeconds}
            />
            <div className="flex justify-end">
              {nextBtn("form")}
            </div>
          </div>
        )}

        {/* ── FORM ── */}
        {activeTab === "form" && (
          <div className="flex flex-col gap-4">
            <SwitchBrick
              checked={includeForm}
              onChange={(v) => {
                setIncludeForm(v);
                if (!v) setSelectedTemplateId(null);
              }}
              title="Inclure un formulaire"
              description="Ajoutez un formulaire structuré à votre compte-rendu."
            />
            {includeForm && (
              <>
                {formTemplates.length > 0 ? (
                  <DropDown
                    label="Modèle de formulaire"
                    value={selectedTemplateId || ""}
                    options={formTemplates.map((t) => ({
                      label: t.name,
                      value: t.id,
                    }))}
                    onSelect={(id) => setSelectedTemplateId(id as string)}
                    placeholder="Sélectionner un modèle..."
                  />
                ) : (
                  <p className="text-h3 text-neutral-400">
                    Aucun modèle de formulaire disponible. Créez-en un dans les
                    paramètres.
                  </p>
                )}
                {!selectedTemplateId && formTemplates.length > 0 && (
                  <p className="text-h3 text-neutral-400">
                    Sélectionnez un modèle pour afficher les champs.
                  </p>
                )}
                {form?.categories.map((cat) => (
                  <div key={cat.id} className="flex flex-col gap-3">
                    <div className="text-h2 font-medium text-neutral-700 mt-2">
                      {cat.name}
                    </div>
                    {cat.fields.map((field) => (
                      <FormFieldBlock
                        key={field.id}
                        field={field}
                        onChange={(value) =>
                          updateFormFieldValue(cat.id, field.id, value)
                        }
                      />
                    ))}
                  </div>
                ))}
              </>
            )}
            <div className="flex justify-between">
              {backBtn("infos")}
              {nextBtn("body")}
            </div>
          </div>
        )}

        {/* ── BODY ── */}
        {activeTab === "body" && (
          <div className="flex flex-col gap-4">
            <p className="text-h3 text-neutral-400">
              Ajoutez des notes ou commentaires libres.
            </p>
            <AreaTextField
              label="Contenu libre"
              value={body}
              onChange={setBody}
              placeholder="Notes, commentaires, observations..."
              rows={6}
            />
            <div className="flex justify-between">
              {backBtn("form")}
              {nextBtn("photos")}
            </div>
          </div>
        )}

        {/* ── PHOTOS ── */}
        {activeTab === "photos" && (
          <div className="flex flex-col gap-4">
            <p className="text-h3 text-neutral-400">
              Choisissez les photos à inclure dans le compte-rendu.
            </p>
            {project.photos.length === 0 ? (
              <p className="text-h3 text-neutral-400">
                Aucune photo disponible sur ce projet.
              </p>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-3">
                {project.photos.map((photo) => {
                  const selected = selectedPhotoIds.includes(photo.id);
                  return (
                    <div
                      key={photo.id}
                      onClick={() =>
                        setSelectedPhotoIds((prev) =>
                          prev.includes(photo.id)
                            ? prev.filter((id) => id !== photo.id)
                            : [...prev, photo.id]
                        )
                      }
                      className={`cursor-pointer rounded-xl overflow-hidden aspect-square border-2 transition-all ${
                        selected
                          ? "border-neutral-900"
                          : "border-transparent opacity-50"
                      }`}
                    >
                      <CustomImage
                        source={{ uri: photo.file.url }}
                        className="w-full h-full object-cover"
                        hidden={photo.hidden}
                        alt=""
                      />
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex justify-between">
              {backBtn("body")}
              {nextBtn("signataires")}
            </div>
          </div>
        )}

        {/* ── SIGNATAIRES ── */}
        {activeTab === "signataires" && (
          <div className="flex flex-col gap-4">
            <p className="text-h3 text-neutral-400">
              Choisissez qui doit signer ce document.
            </p>

            {/* Moi */}
            <div className="flex flex-col gap-2">
              <div className="text-h2 font-medium text-neutral-700">Moi</div>
              <SwitchBrick
                checked={!!signatures.find((s) => s.id === conductorId)}
                onChange={toggleConductorSignature}
                title={conductorName}
                description={
                  signatures.find((s) => s.id === conductorId)?.status === "signed_in_app"
                    ? "✓ Signature enregistrée appliquée"
                    : userData?.infos?.signature_id
                    ? "Signature enregistrée disponible — sera appliquée automatiquement"
                    : "Inclure ma signature dans le document"
                }
              />
              {signatures.find((s) => s.id === conductorId) && !userData?.infos?.signature_id && !uploadedSignatureUrl && (
                <div className="flex flex-col gap-2 p-3 bg-orange-50 border border-orange-200 rounded-xl">
                  <div className="text-sm text-orange-700">
                    Aucune signature enregistrée. Ajoutez votre signature pour signer le document.
                  </div>
                  <input
                    ref={signatureFileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleSignatureUpload}
                    className="hidden"
                  />
                  <Button
                    label={isUploadingSignature ? "Téléchargement..." : "Ajouter ma signature"}
                    variant="small"
                    disabled={isUploadingSignature}
                    onClick={handleSignatureButtonClick}
                  />
                </div>
              )}
              {signatures.find((s) => s.id === conductorId)?.status === "signed_in_app" &&
                (userData?.infos?.signature_id || uploadedSignatureUrl) && (
                  <div className="border rounded-xl p-3 bg-neutral-50">
                    <img
                      src={uploadedSignatureUrl || userData?.infos?.signature_id || ""}
                      alt="Votre signature"
                      className="max-h-16 object-contain"
                    />
                  </div>
                )}
            </div>

            {/* Clients */}
            {clients.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="text-h2 font-medium text-neutral-700">
                  Clients
                </div>
                {clients.map((contact) => {
                  const sigId = `client_${contact.email || contact.last_name}`;
                  return (
                    <SwitchBrick
                      key={sigId}
                      checked={!!signatures.find((s) => s.id === sigId)}
                      onChange={() =>
                        toggleSignature(sigId, {
                          signer_type: "client",
                          signer_id: sigId,
                          signer_name: `${contact.first_name} ${contact.last_name}`,
                          signer_email: contact.email,
                        })
                      }
                      title={`${contact.first_name} ${contact.last_name}`}
                      description={contact.email || "Inclure comme signataire"}
                    />
                  );
                })}
              </div>
            )}

            {/* Artisans */}
            {partners.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="text-h2 font-medium text-neutral-700">
                  Artisans
                </div>
                {partners
                  .filter((partner) => partner.id !== conductorId)
                  .map((partner) => {
                    const sigId = `partner_${partner.id || partner.display_name}`;
                    return (
                      <SwitchBrick
                        key={sigId}
                        checked={!!signatures.find((s) => s.id === sigId)}
                        onChange={() =>
                          toggleSignature(sigId, {
                            signer_type: "partner",
                            signer_id: partner.id,
                            signer_name: partner.display_name,
                            signer_email: partner.email,
                          })
                        }
                        title={partner.display_name}
                        description={partner.email || "Inclure comme signataire"}
                      />
                    );
                  })}
              </div>
            )}

            {clients.length === 0 && partners.length === 0 && (
              <p className="text-h3 text-neutral-400">
                Aucun contact ou intervenant associé à ce projet.
              </p>
            )}

            <div className="flex justify-between">
              {backBtn("photos")}
              {nextBtn("generer")}
            </div>
          </div>
        )}

        {/* ── GÉNÉRER ── */}
        {activeTab === "generer" && (
          <div className="flex flex-col gap-4">
            {folderOptions.length > 0 ? (
              <DropDown
                label="Dossier de destination"
                value={selectedFolderId || ""}
                options={folderOptions}
                onSelect={(v) => setSelectedFolderId(v as string)}
                placeholder="Sélectionner un dossier..."
              />
            ) : (
              <p className="text-h3 text-neutral-400">
                Aucun dossier disponible sur ce projet.
              </p>
            )}

            {/* Summary */}
            <div className="bg-neutral-100 rounded-xl p-4 flex flex-col gap-3 border border-neutral-200">
              <div className="text-h2 font-medium text-neutral-700">{title}</div>
              <SummaryRow label="Projet" value={projectName} />
              <SummaryRow label="Adresse" value={fullAddress} />
              <SummaryRow
                label="Photos"
                value={`${selectedPhotoIds.length} photo(s)`}
              />
              <SummaryRow
                label="Formulaire"
                value={
                  form
                    ? formTemplates.find((t) => t.id === form.id)?.name ||
                      "Formulaire inclus"
                    : "Aucun formulaire"
                }
              />
              <SummaryRow
                label="Signataires"
                value={
                  signatures.length === 0
                    ? "Aucun"
                    : signatures.map((s) => s.signer_name).join(", ")
                }
              />
              <SummaryRow
                label="Destination"
                value={
                  folderOptions.find((f) => f.value === selectedFolderId)
                    ?.label || "Aucun dossier sélectionné"
                }
              />
            </div>

            <div className="flex justify-between">
              {backBtn("signataires")}
              <Button
                label={
                  isGenerating ? "Génération en cours..." : "Générer le document"
                }
                onClick={handleGenerate}
              />
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
};

// ── FormFieldBlock ──

const FormFieldBlock = ({
  field,
  onChange,
}: {
  field: FormField;
  onChange: (value: any) => void;
}) => {
  switch (field.type) {
    case "text":
      return (
        <TextField
          label={field.label}
          value={field.value as string}
          onChange={onChange}
        />
      );
    case "number":
      return (
        <TextField
          label={field.label}
          value={String(field.value ?? "")}
          type="number"
          onChange={onChange}
        />
      );
    case "date":
      return (
        <DatePicker
          label={field.label}
          seconds={
            typeof field.value === "number"
              ? (field.value as number)
              : Math.floor(Date.now() / 1000)
          }
          onChange={onChange}
        />
      );
    case "checkbox":
      return (
        <CheckBoxInput
          label={field.label}
          checked={field.value as boolean}
          onChange={(e) => onChange(e.target.checked)}
        />
      );
    case "select":
      return (
        <DropDown
          label={field.label}
          value={field.value as string}
          options={(field.options || []).map((opt) => ({
            label: opt,
            value: opt,
          }))}
          onSelect={onChange}
        />
      );
    case "multiselect": {
      const selected = Array.isArray(field.value)
        ? (field.value as string[])
        : [];
      return (
        <div className="flex flex-col gap-2">
          <div className="text-h4 text-neutral-400">{field.label}</div>
          <div className="flex flex-wrap gap-2">
            {(field.options || []).map((opt) => {
              const isSelected = selected.includes(opt);
              return (
                <button
                  key={opt}
                  onClick={() => {
                    const next = isSelected
                      ? selected.filter((v) => v !== opt)
                      : [...selected, opt];
                    onChange(next);
                  }}
                  className={`px-3 py-1 rounded-full text-h3 border transition-all ${
                    isSelected
                      ? "bg-neutral-900 text-white border-neutral-900"
                      : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500"
                  }`}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    default:
      return null;
  }
};

// ── SummaryRow ──

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-row gap-4">
    <span className="text-h3 text-neutral-400 w-24 shrink-0">{label}</span>
    <span className="text-h3 text-neutral-700 flex-1">{value}</span>
  </div>
);
